// Run replays: every tick's command from a restart, so the hiscore server can play the run again
// with the same simulation and check the time (see verify.ts).
//
// View angles go into the simulation as float32 values (Math.fround) so a replay can store them
// exactly in 4 bytes. Movement is unchanged: the angles only lose digits far below a mouse count.
//
// Binary format (then deflated):
//   "BHR1", u8 flags (1 autobhop, 2 slopeSlide, 4 jump held at the start), u16 sv_airaccelerate,
//   u32 ticks, then per column: buttons u16[n], weaponSelect u8[n], yaw and pitch as zigzag varint
//   deltas of their float32 bit patterns (small turns give small numbers).

import { deflate, deflateSync, inflateSync } from 'fflate';
import type { UserCmd } from '../sim/input';

/** Longest run that can be recorded: 30 minutes at 100 tick. */
export const MAX_REPLAY_TICKS = 30 * 60 * 100;

export interface ReplayRules {
  airaccel: number;
  autobhop: boolean;
  slopeSlide: boolean;
}

export interface Replay extends ReplayRules {
  /** Jump was held when the run started (matters without autobhop). */
  jumpHeld: boolean;
  ticks: number;
  buttons: Uint16Array;
  weapon: Uint8Array;
  yaw: Float32Array;
  pitch: Float32Array;
}

/** Records commands from a restart. Allocates once; record() is safe to call every tick. */
export class RunRecorder {
  private readonly buttons = new Uint16Array(MAX_REPLAY_TICKS);
  private readonly weapon = new Uint8Array(MAX_REPLAY_TICKS);
  private readonly yaw = new Float32Array(MAX_REPLAY_TICKS);
  private readonly pitch = new Float32Array(MAX_REPLAY_TICKS);
  private n = 0;
  private rules: ReplayRules = { airaccel: 100, autobhop: true, slopeSlide: true };
  private jumpHeld = false;
  /** False after anything a replay can't reproduce (noclip, a too long run). */
  valid = false;

  /** A new run starts (right after Simulation.restart()). */
  start(rules: ReplayRules, jumpHeld: boolean) {
    this.rules = { ...rules };
    this.jumpHeld = jumpHeld;
    this.n = 0;
    this.valid = true;
  }

  invalidate() {
    this.valid = false;
  }

  /** Rounds the command's angles to float32 (always, so play matches replay) and records it. */
  record(cmd: UserCmd) {
    cmd.yaw = Math.fround(cmd.yaw);
    cmd.pitch = Math.fround(cmd.pitch);
    if (!this.valid) return;
    const i = this.n;
    if (i >= MAX_REPLAY_TICKS) {
      this.valid = false;
      return;
    }
    this.buttons[i] = cmd.buttons;
    this.weapon[i] = cmd.weaponSelect;
    this.yaw[i] = cmd.yaw;
    this.pitch[i] = cmd.pitch;
    this.n = i + 1;
  }

  get ticks() {
    return this.n;
  }

  /** The run so far as a replay (copies; call when the run finished). */
  snapshot(): Replay | null {
    if (!this.valid || this.n === 0) return null;
    const n = this.n;
    return {
      ...this.rules,
      jumpHeld: this.jumpHeld,
      ticks: n,
      buttons: this.buttons.slice(0, n),
      weapon: this.weapon.slice(0, n),
      yaw: this.yaw.slice(0, n),
      pitch: this.pitch.slice(0, n),
    };
  }
}

/** Fill `out` with tick `i` of a replay (forwardmove/sidemove come from the buttons). */
export function replayCmd(r: Replay, i: number, out: UserCmd, forwardspeed: number): UserCmd {
  const b = r.buttons[i];
  out.buttons = b;
  out.forwardmove = (((b >> 3) & 1) - ((b >> 4) & 1)) * forwardspeed;
  out.sidemove = (((b >> 10) & 1) - ((b >> 9) & 1)) * forwardspeed;
  out.weaponSelect = r.weapon[i];
  out.yaw = r.yaw[i];
  out.pitch = r.pitch[i];
  return out;
}

const MAGIC = [0x42, 0x48, 0x52, 0x31]; // "BHR1"

export function encodeReplay(r: Replay): Uint8Array {
  return deflateSync(packReplay(r), { level: 9 });
}

/** Same as encodeReplay, compressed in a worker (no stall when a long run finishes). */
export function encodeReplayAsync(r: Replay): Promise<Uint8Array> {
  const raw = packReplay(r);
  return new Promise((resolve) =>
    deflate(raw, { level: 9 }, (err, out) => resolve(err ? deflateSync(raw, { level: 9 }) : out)),
  );
}

/** The uncompressed binary replay. */
function packReplay(r: Replay): Uint8Array {
  const n = r.ticks;
  // Worst case: 5 bytes per varint.
  const out = new Uint8Array(11 + n * 3 + n * 10);
  let o = 0;
  for (const m of MAGIC) out[o++] = m;
  out[o++] = (r.autobhop ? 1 : 0) | (r.slopeSlide ? 2 : 0) | (r.jumpHeld ? 4 : 0);
  out[o++] = r.airaccel & 0xff;
  out[o++] = r.airaccel >>> 8;
  out[o++] = n & 0xff;
  out[o++] = (n >>> 8) & 0xff;
  out[o++] = (n >>> 16) & 0xff;
  out[o++] = n >>> 24;
  for (let i = 0; i < n; i++) {
    out[o++] = r.buttons[i] & 0xff;
    out[o++] = r.buttons[i] >>> 8;
  }
  for (let i = 0; i < n; i++) out[o++] = r.weapon[i];
  for (const col of [r.yaw, r.pitch]) {
    const bits = new Uint32Array(col.buffer, col.byteOffset, n);
    let prev = 0;
    for (let i = 0; i < n; i++) {
      const d = (bits[i] - prev) | 0;
      prev = bits[i];
      let z = ((d << 1) ^ (d >> 31)) >>> 0;
      while (z >= 0x80) {
        out[o++] = (z & 0x7f) | 0x80;
        z >>>= 7;
      }
      out[o++] = z;
    }
  }
  return out.subarray(0, o);
}

/** Throws on anything that isn't a well-formed replay. */
export function decodeReplay(data: Uint8Array): Replay {
  let raw: Uint8Array;
  try {
    raw = inflateSync(data);
  } catch {
    throw new Error('Replay is not compressed data');
  }
  if (raw.length < 11 || MAGIC.some((m, i) => raw[i] !== m)) throw new Error('Not a replay');
  const flags = raw[4];
  const airaccel = raw[5] | (raw[6] << 8);
  const n = (raw[7] | (raw[8] << 8) | (raw[9] << 16) | (raw[10] << 24)) >>> 0;
  if (n === 0 || n > MAX_REPLAY_TICKS) throw new Error('Replay length out of range');
  let o = 11;
  if (raw.length < o + n * 3) throw new Error('Replay is cut short');
  const buttons = new Uint16Array(n);
  for (let i = 0; i < n; i++, o += 2) buttons[i] = raw[o] | (raw[o + 1] << 8);
  const weapon = raw.slice(o, o + n);
  o += n;
  const cols = [new Float32Array(n), new Float32Array(n)];
  for (const col of cols) {
    const bits = new Uint32Array(col.buffer);
    let prev = 0;
    for (let i = 0; i < n; i++) {
      let z = 0;
      let shift = 0;
      for (;;) {
        if (o >= raw.length || shift > 28) throw new Error('Replay is cut short');
        const b = raw[o++];
        z |= (b & 0x7f) << shift;
        if (b < 0x80) break;
        shift += 7;
      }
      z >>>= 0;
      const d = (z >>> 1) ^ -(z & 1);
      prev = (prev + d) >>> 0;
      bits[i] = prev;
    }
    for (let i = 0; i < n; i++) if (!Number.isFinite(col[i])) throw new Error('Replay has a broken angle');
  }
  if (o !== raw.length) throw new Error('Replay has extra data');
  return {
    autobhop: (flags & 1) !== 0,
    slopeSlide: (flags & 2) !== 0,
    jumpHeld: (flags & 4) !== 0,
    airaccel,
    ticks: n,
    buttons,
    weapon,
    yaw: cols[0],
    pitch: cols[1],
  };
}

/** Hiscore category for these rules: "aa1337-auto", "aa100-legit-noslide"... */
export function rulesKey(r: ReplayRules): string {
  return `aa${r.airaccel}-${r.autobhop ? 'auto' : 'legit'}${r.slopeSlide ? '' : '-noslide'}`;
}

export function rulesLabel(r: ReplayRules): string {
  return `sv_airaccelerate ${r.airaccel} · autobhop ${r.autobhop ? 'on' : 'off'}${r.slopeSlide ? '' : ' · no slope slide'}`;
}
